import React from 'react';

export const DEFAULT_ECHARTS_HTML_HEIGHT = 480;
export const DEFAULT_ECHARTS_HTML_SANDBOX = 'allow-scripts';

/** Backend-hosted ECharts script paths (air-gapped UIs). */
export interface AgenticEChartsChartAssets {
    echarts_js?: string;
    renderer_js?: string;
}

/** How the backend expects the HTML chart document to be embedded. */
export interface AgenticEChartsChartRender {
    engine?: string;
    mode?: string;
    height?: number;
    sandbox?: string;
}

/**
 * Sibling fields on a backend chart payload (alongside `viz_data`).
 * The HTML document already includes Asper styles, `askasper_echarts_gallery.js`,
 * and the chart’s `viz_data`. No React chart component is required.
 */
export interface AgenticHtmlChartFields {
    engine?: string;
    html?: string;
    assets?: AgenticEChartsChartAssets;
    render?: AgenticEChartsChartRender;
}

export interface EChartsHtmlChartProps {
    html: string;
    title?: string;
    height?: number;
    sandbox?: string;
    className?: string;
    'data-test-id'?: string;
}

export function hasHtmlChartDocument(source: unknown): boolean {
    if (!source || typeof source !== 'object') return false;
    const html = (source as { html?: unknown }).html;
    return typeof html === 'string' && html.trim().length > 0;
}

export function pickHtmlChartFields(source: unknown): AgenticHtmlChartFields {
    if (!source || typeof source !== 'object') return {};
    const s = source as Record<string, unknown>;
    return {
        html: typeof s.html === 'string' ? s.html : undefined,
        engine: typeof s.engine === 'string' ? s.engine : undefined,
        assets: s.assets && typeof s.assets === 'object' ? (s.assets as AgenticEChartsChartAssets) : undefined,
        render: s.render && typeof s.render === 'object' ? (s.render as AgenticEChartsChartRender) : undefined,
    };
}

/**
 * Renders a backend-produced ECharts HTML document in a sandboxed iframe.
 * Scripts run inside the iframe (unique origin; no `allow-same-origin`).
 */
const EChartsHtmlChart: React.FC<EChartsHtmlChartProps> = ({
    html,
    title = 'Chart',
    height = DEFAULT_ECHARTS_HTML_HEIGHT,
    sandbox = DEFAULT_ECHARTS_HTML_SANDBOX,
    className,
    'data-test-id': dataTestId,
}) => {
    if (!html.trim()) return null;

    return (
        <iframe
            srcDoc={html}
            title={title}
            sandbox={sandbox}
            className={className}
            style={{ width: '100%', height, border: 0, display: 'block' }}
            data-test-id={dataTestId}
            data-echarts-html-chart
        />
    );
};

export function renderEChartsHtmlIfPresent(
    fields: { html?: string; vizData?: any; render?: AgenticEChartsChartRender },
    dataTestId?: string,
): React.ReactNode {
    if (typeof fields.html !== 'string' || !fields.html.trim()) return null;
    const title =
        typeof fields.vizData?.chartTitle === 'string' && fields.vizData.chartTitle.trim()
            ? fields.vizData.chartTitle
            : 'Chart';
    return (
        <EChartsHtmlChart
            html={fields.html}
            title={title}
            height={typeof fields.render?.height === 'number' ? fields.render.height : undefined}
            sandbox={typeof fields.render?.sandbox === 'string' ? fields.render.sandbox : undefined}
            data-test-id={dataTestId}
        />
    );
}

export default EChartsHtmlChart;
